import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Crosshair, Wallet, Banknote, Smartphone, Loader } from 'lucide-react';
import marketApi from './marketApi';
import { useCart } from './CartContext';
import { useUsdRate, usdLabel } from './usdRate';
import { PrescriptionFiles } from './PharmacyPages';

/**
 * Checkout livraison (web) — même contrat que l'app native :
 *  • adresse + coordonnées (géoloc navigateur ou saisie) → estimation serveur
 *    (frais, seuils cart_min / mm_required_threshold, surcharge MM) ;
 *  • paiements : espèces à la livraison, solde Mbiyo, Mobile Money PAY-FIRST
 *    (initiation SerdiPay + attente de confirmation, puis création de commande
 *    avec serdipay_payment_id — le backend re-tarife et vérifie tout).
 */
export default function CheckoutPage({ nav, currentUser, citySlug }) {
  const { cart, subtotal, clearCart, lineTotal } = useCart();
  const usdRate = useUsdRate();
  const [shopPromos, setShopPromos] = useState([]); // promos boutique (remise au seuil)
  // PHARMACIE : photos d'ordonnance (envoyées AVANT tout paiement) + frais de retour
  const [rxFiles, setRxFiles] = useState([]);
  const [rxPhotoIds, setRxPhotoIds] = useState(null);

  const [address, setAddress] = useState('');
  const [details, setDetails] = useState('');
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);

  const [est, setEst] = useState(null);
  const [estErr, setEstErr] = useState(null);
  const [cartMin, setCartMin] = useState(5000);
  const [mmThreshold, setMmThreshold] = useState(150000);

  const [pay, setPay] = useState('cash');
  const [balance, setBalance] = useState(null);
  const [phone, setPhone] = useState('');
  const [telecom, setTelecom] = useState('AM');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [mmPhase, setMmPhase] = useState(null); // texte pendant le paiement MM
  const [error, setError] = useState(null);

  // Remise au seuil — miroir serveur (appliquée après les remises lignes,
  // qui sont déjà dans `subtotal`). Le serveur refait ce calcul à la commande.
  const promoDiscount = shopPromos.reduce((best, p) =>
    (p.type === 'threshold' && p.min_amount > 0 && subtotal >= p.min_amount)
      ? Math.max(best, Math.min(p.discount_amount || 0, subtotal)) : best, 0);
  const paidSubtotal = subtotal - promoDiscount;

  const deliveryFee = est?.delivery_fee || 0;
  // Pharmacie — miroir serveur : retour de l'original UNIQUEMENT si un produit
  // l'exige (même trajet, même barème que la livraison), inclus dans la base MM.
  const cartNeedsRx = !!cart && !cart.quote_id && cart.items.some(i => i.rx_required);
  const cartNeedsOriginal = !!cart && cart.items.some(i => i.rx_original_return);
  const rxReturnFee = cartNeedsOriginal ? (est?.rx_return_fee ?? deliveryFee) : 0;
  const mmRate = 0; // frais Mobile Money retirés des apps (31 oct. 2026)
  const mmSurcharge = pay === 'mobile_money' ? Math.round((paidSubtotal + deliveryFee + rxReturnFee) * mmRate) : 0;
  const total = paidSubtotal + deliveryFee + rxReturnFee + mmSurcharge;
  const cashBlocked = paidSubtotal >= mmThreshold;

  // Promotions boutique actives (remise au seuil)
  useEffect(() => {
    if (!cart?.supplierId) return;
    let alive = true;
    marketApi.promosActive(cart.supplierId)
      .then(r => { if (alive && r?.success) setShopPromos(r.promos || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [cart?.supplierId]);

  // Géolocalisation
  const locate = () => {
    if (!navigator.geolocation) { setError('Géolocalisation indisponible sur ce navigateur.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocating(false); if (!address) setAddress('Ma position actuelle'); },
      () => { setLocating(false); setError('Position refusée — saisissez votre adresse et vos coordonnées de quartier.'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  useEffect(() => { locate(); /* tentative silencieuse à l'ouverture */ }, []); // eslint-disable-line

  // Estimation serveur dès que les coordonnées sont connues
  useEffect(() => {
    if (!cart || !coords) return;
    let alive = true;
    (async () => {
      setEstErr(null);
      try {
        const r = await marketApi.estimate({
          supplier_id: cart.supplierId,
          delivery_lat: coords.lat, delivery_lng: coords.lng,
          payment_method: pay, subtotal,
        });
        if (!alive) return;
        if (r.success) {
          setEst(r);
          if (r.cart_min) setCartMin(r.cart_min);
          if (r.mm_required_threshold) setMmThreshold(r.mm_required_threshold);
        } else setEstErr(r.error || 'Estimation impossible');
      } catch (e) { if (alive) setEstErr(e?.error || e?.message || 'Estimation impossible'); }
    })();
    return () => { alive = false; };
  }, [cart, coords, pay, subtotal]);

  // Solde (pour l'option « solde Mbiyo »)
  useEffect(() => {
    if (!currentUser?.user_id) return;
    marketApi.myBalance(currentUser.user_id)
      .then(r => { if (r?.success) setBalance(r.balance?.current_balance ?? null); })
      .catch(() => {});
  }, [currentUser?.user_id]);

  // Cash interdit au-delà du seuil → bascule automatique
  useEffect(() => { if (cashBlocked && pay === 'cash') setPay('balance'); }, [cashBlocked, pay]);

  const canSubmit = cart && coords && address.trim() && est && paidSubtotal >= cartMin && !submitting;

  const buildOrder = (extra = {}) => ({
    supplier_id: cart.supplierId,
    items: cart.items.map(i => ({
      product_id: i.product_id,
      product_name: i.name,
      quantity: i.quantity,
      unit_price: (i.promo_price || i.price) + (i.options_total || 0),
      total_price: ((i.promo_price || i.price) + (i.options_total || 0)) * i.quantity,
      unit: i.unit || 'piece',
      selected_options: (i.options || []).map(o => ({ group_id: o.group_id, option_id: o.option_id })),
      special_instructions: i.special_instructions || '',
    })),
    delivery_address: address.trim(),
    delivery_details: details.trim(),
    delivery_lat: coords.lat,
    delivery_lng: coords.lng,
    payment_method: pay,
    notes: notes.trim(),
    ...(extra.prescription_photo_ids ? {} : (rxPhotoIds ? { prescription_photo_ids: rxPhotoIds } : {})),
    ...(cart.quote_id ? { quote_id: cart.quote_id } : {}),
    ...(citySlug ? { city_slug: citySlug } : {}),
    ...extra,
  });

  const finishOk = (orderId) => { clearCart(); nav({ page: 'order', orderId }); };

  const submit = async () => {
    setError(null);
    if (!canSubmit) return;
    if (cartNeedsRx && !rxFiles.length) { setError('Ordonnance requise : ajoutez la photo de votre ordonnance.'); return; }
    setSubmitting(true);
    try {
      // Ordonnance envoyée AVANT tout paiement : un échec d'envoi ne laisse
      // jamais un paiement Mobile Money sans commande.
      let photoIds = rxPhotoIds;
      if (cartNeedsRx && !photoIds) {
        photoIds = await marketApi.uploadPrescriptions(rxFiles);
        setRxPhotoIds(photoIds);
      }
      const rxExtra = photoIds ? { prescription_photo_ids: photoIds } : {};
      if (pay === 'mobile_money') {
        // 1) PAY-FIRST — initiation SerdiPay (proxy backend) puis attente.
        const num = phone.replace(/\D/g, '');
        if (num.length < 9) { setError('Numéro Mobile Money invalide (ex. 0991234567).'); setSubmitting(false); return; }
        const intl = num.startsWith('243') ? num : `243${num.replace(/^0/, '')}`;
        setMmPhase('Envoi de la demande de paiement…');
        const init = await marketApi.mmInitiate({
          user_id: currentUser.user_id,
          plan_type: 'delivery_order',
          plan_name: `Commande ${cart.supplierName}`,
          amount: total, currency: 'CDF', payment_currency: 'CDF',
          phone_number: intl, telecom,
        });
        if (!init?.success || !init.payment_id) throw new Error(init?.error || 'Initiation du paiement impossible');
        setMmPhase('Confirmez le paiement sur votre téléphone…');
        // 2) Polling du statut (jusqu'à ~2 min, comme le natif)
        let status = 'pending';
        for (let i = 0; i < 40; i++) {
          await new Promise(r => setTimeout(r, 3000));
          const st = await marketApi.mmStatus(init.payment_id).catch(() => null);
          status = st?.payment?.status || status;
          if (status === 'completed' || status === 'failed') break;
        }
        if (status !== 'completed') throw new Error('Paiement non confirmé. Aucun montant ne sera prélevé sans confirmation.');
        setMmPhase('Paiement confirmé — création de la commande…');
        const res = await marketApi.createOrder(buildOrder({
          ...rxExtra,
          serdipay_payment_id: init.payment_id,
          payment_phone: `+${intl}`, telecom, payment_currency: 'CDF',
        }));
        if (!res.success) throw new Error(res.error || 'Commande impossible');
        finishOk(res.order.order_id);
        return;
      }
      const res = await marketApi.createOrder(buildOrder(rxExtra));
      if (!res.success) throw new Error(res.error || 'Commande impossible');
      finishOk(res.order.order_id);
    } catch (e) {
      setError(e?.error || e?.message || 'Erreur réseau');
    } finally {
      setSubmitting(false);
      setMmPhase(null);
    }
  };

  const payOptions = useMemo(() => ([
    { key: 'cash', label: 'Espèces à la livraison', icon: <Banknote size={20} />, disabled: cashBlocked },
    { key: 'balance', label: `Solde Mbiyo${balance != null ? ` (${balance.toLocaleString('fr-FR')} FC)` : ''}`, icon: <Wallet size={20} />, disabled: false },
    { key: 'mobile_money', label: 'Mobile Money', icon: <Smartphone size={20} />, disabled: false },
  ]), [cashBlocked, balance]);

  if (!cart?.items?.length) {
    return <div className="mk-container"><div className="mk-center">Panier vide. <button className="mk-btn primary" onClick={() => nav({ page: 'home' })}>Retour au marché</button></div></div>;
  }

  return (
    <div className="mk-container">
      <div className="mk-two">
        {/* ── Adresse + paiement ── */}
        <div>
          <div className="mk-panel">
            <h3><MapPin size={17} style={{ verticalAlign: '-3px' }} /> Adresse de livraison</h3>
            <div className="mk-field">
              <label>Adresse *</label>
              <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Ex. Av. Lumumba 12, Q/ Ndendere" />
            </div>
            <div className="mk-field">
              <label>Précisions pour le livreur</label>
              <input value={details} onChange={e => setDetails(e.target.value)} placeholder="Repère, étage, porte…" />
            </div>
            <button className="mk-btn ghost" onClick={locate} disabled={locating}>
              {locating ? <Loader size={16} className="spin" /> : <Crosshair size={16} />}
              {coords ? 'Position détectée ✓ — actualiser' : 'Utiliser ma position'}
            </button>
            {!coords && <p className="mk-note warn" style={{ marginTop: 10 }}>La position GPS est nécessaire pour calculer les frais et guider le livreur — autorisez-la, ou commandez depuis l'app mobile.</p>}
            {estErr && <p className="mk-note err" style={{ marginTop: 10 }}>{estErr}</p>}
          </div>

          {cartNeedsRx && (
            <div className="mk-panel" style={{ marginTop: 16 }}>
              <h3>📋 Ordonnance *</h3>
              <p className="mk-desc">Ces produits sont délivrés sur ordonnance : la pharmacie l'examinera avant d'accepter votre commande.</p>
              <PrescriptionFiles files={rxFiles} onChange={(f) => { setRxFiles(f); setRxPhotoIds(null); }} />
            </div>
          )}
          {cartNeedsOriginal && (
            <p className="mk-note warn" style={{ marginTop: 16 }}>📄 Remettez l'ordonnance ORIGINALE au livreur à la livraison : il la rapporte à la pharmacie (frais de retour au récapitulatif).</p>
          )}

          <div className="mk-panel" style={{ marginTop: 16 }}>
            <h3>Paiement</h3>
            <div className="mk-pay">
              {payOptions.map(o => (
                <button key={o.key} className={pay === o.key ? 'on' : ''} disabled={o.disabled} onClick={() => setPay(o.key)}>
                  {o.icon}{o.label}
                </button>
              ))}
            </div>
            {cashBlocked && <p className="mk-note warn" style={{ marginTop: 10 }}>Au-delà de {mmThreshold.toLocaleString('fr-FR')} FC, le paiement à la livraison n'est pas disponible.</p>}
            {pay === 'mobile_money' && (
              <div style={{ marginTop: 12 }}>
                <div className="mk-field">
                  <label>Numéro Mobile Money *</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="0991234567" inputMode="tel" />
                </div>
                <div className="mk-field">
                  <label>Opérateur</label>
                  <select value={telecom} onChange={e => setTelecom(e.target.value)}>
                    <option value="AM">Airtel Money</option>
                    <option value="MP">M-Pesa (Vodacom)</option>
                    <option value="OM">Orange Money</option>
                  </select>
                </div>
                <p className="mk-note">Vous recevrez une demande de confirmation sur votre téléphone. La commande n'est créée qu'après paiement confirmé — en cas d'échec ultérieur, le montant est automatiquement recrédité sur votre solde Mbiyo.</p>
              </div>
            )}
            <div className="mk-field" style={{ marginTop: 12 }}>
              <label>Note pour le commerce (facultatif)</label>
              <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Ex. appeler en arrivant" />
            </div>
          </div>
        </div>

        {/* ── Récapitulatif ── */}
        <div className="mk-panel">
          <h3>Votre commande — {cart.supplierName}</h3>
          {cart.items.map(i => (
            <div className="mk-total" key={i.line_id}>
              <span>{i.quantity}× {i.name}{i.options?.length ? ` (+${i.options.length} opt.)` : ''}</span>
              <span>{lineTotal(i).toLocaleString('fr-FR')} FC</span>
            </div>
          ))}
          <div className="mk-total" style={{ borderTop: '1px solid var(--mb-border)', marginTop: 6, paddingTop: 10 }}>
            <span>Sous-total</span><span>{subtotal.toLocaleString('fr-FR')} FC</span>
          </div>
          {promoDiscount > 0 && (
            <div className="mk-total" style={{ color: 'var(--mb-success)', fontWeight: 700 }}>
              <span>🏷️ Remise promotion</span><span>−{promoDiscount.toLocaleString('fr-FR')} FC</span>
            </div>
          )}
          <div className="mk-total">
            <span>Frais de livraison{est?.distance_km ? ` (${est.distance_km} km)` : ''}</span>
            <span>{est ? `${deliveryFee.toLocaleString('fr-FR')} FC` : '—'}</span>
          </div>
          {rxReturnFee > 0 && <div className="mk-total"><span>📄 Retour de l'ordonnance à la pharmacie</span><span>{rxReturnFee.toLocaleString('fr-FR')} FC</span></div>}
          <div className="mk-total grand"><span>Total</span><span>{total.toLocaleString('fr-FR')} FC</span></div>
          {usdLabel(total, usdRate) && <div className="mk-total" style={{ color: 'var(--mb-text-muted, #6B7280)', fontSize: 13 }}><span>Équivalent indicatif (paiement en FC)</span><span>{usdLabel(total, usdRate)}</span></div>}
          {est?.estimated_time && <p className="mk-note" style={{ marginTop: 10 }}>⏱ Livraison estimée : ~{est.estimated_time} min</p>}
          {paidSubtotal < cartMin && <p className="mk-note warn" style={{ marginTop: 10 }}>Minimum de commande : {cartMin.toLocaleString('fr-FR')} FC.</p>}
          {error && <p className="mk-note err" style={{ marginTop: 10 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 14 }} disabled={!canSubmit} onClick={submit}>
            {submitting
              ? (mmPhase || 'Création de la commande…')
              : `Commander — ${total.toLocaleString('fr-FR')} FC`}
          </button>
        </div>
      </div>
    </div>
  );
}
